import { useCallback, useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { CalendarDays, SkipForward, Undo2 } from "lucide-react";
import { useClientPerms } from "../lib/useClientPerms";

const FREQ = { weekly: "Weekly", biweekly: "Bi-weekly", semimonthly: "Semi-monthly", monthly: "Monthly" };
const fmt = (iso) => iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "—";

// Next six pay dates per schedule, with "no payroll" skip / undo.
export default function PayCalendarPanel({ companyId }) {
  const [data, setData] = useState(null);
  const { canEdit, isClient } = useClientPerms();
  const canSkip = canEdit("run_payroll");
  const load = useCallback(() => { if (companyId) api.get(`/companies/${companyId}/pay-calendar`).then((r) => setData(r.data)).catch(() => setData(null)); }, [companyId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const today = new Date().toISOString().slice(0, 10);

  const skip = async (s) => {
    const reason = window.prompt(`Mark the next ${s.schedule_name || FREQ[s.pay_frequency]} period (pay date ${s.upcoming[0].pay_date}) as NO PAYROLL?\nThe schedule will advance to the following period. Enter a reason:`);
    if (reason === null) return;
    try { await api.post(`/companies/${companyId}/pay-calendar/skip`, { schedule_name: s.schedule_name, pay_frequency: s.pay_frequency, reason }); toast.success("Period skipped — schedule advanced"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const unskip = async (s, sk) => {
    if (!window.confirm(`Undo the skip for pay date ${sk.pay_date}?`)) return;
    try { await api.delete(`/companies/${companyId}/pay-calendar/skip/${sk.skip_id}`); toast.success("Skip undone"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="pay-calendar" className="border border-border bg-white mb-6">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        <CalendarDays className="h-4 w-4" />
        <h2 className="font-bold tracking-tight">Pay Calendar</h2>
        <span className="text-xs text-muted-foreground">Next six pay dates per schedule · Due by = processor deadline (inputs after this date may delay pay)</span>
      </div>
      <div className={`grid gap-0 ${data.schedules.length > 1 ? "lg:grid-cols-2" : ""}`}>
        {data.schedules.map((s, si) => (
          <div key={si} data-testid={`pay-calendar-schedule-${s.schedule_name || s.pay_frequency}`} className={`px-5 py-4 ${si > 0 ? "border-t lg:border-t-0 lg:border-l border-border" : ""}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{s.schedule_name || "Default schedule"}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5">{FREQ[s.pay_frequency]}</span>
              {s.based_on && <span className="text-[11px] text-muted-foreground">sequenced after {s.based_on}{s.based_on_status === "SKIPPED" ? " (skipped)" : ""}</span>}
              {canSkip && <button data-testid={`pay-calendar-skip-${s.schedule_name || s.pay_frequency}`} onClick={() => skip(s)} className="ml-auto inline-flex items-center gap-1 text-xs border border-border px-2 py-1 hover:border-black transition-colors" title="Mark the next period as no payroll"><SkipForward className="h-3 w-3" /> Skip next period</button>}
            </div>
            <table className="w-full text-sm mt-3">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground border-b border-border"><th className="py-1.5 pr-3">#</th><th className="py-1.5 pr-3">Pay period</th><th className="py-1.5 pr-3">Due by</th><th className="py-1.5">Pay date</th></tr></thead>
              <tbody>
                {s.upcoming.map((u) => (
                  <tr key={u.n} data-testid={`pay-calendar-row-${s.schedule_name || s.pay_frequency}-${u.n}`} className={`border-b border-border/60 ${u.n === 1 ? "font-medium" : ""}`}>
                    <td className="py-1.5 pr-3 font-mono text-xs text-muted-foreground">{u.n}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{u.pay_period_start} → {u.pay_period_end}</td>
                    <td className={`py-1.5 pr-3 font-mono text-xs ${u.past_due_by ? "text-red-700 font-semibold" : ""}`} data-testid={`pay-calendar-due-${s.schedule_name || s.pay_frequency}-${u.n}`} title={`Processor deadline: ${u.due_by_days} business day(s) before pay date`}>{u.due_by}{u.past_due_by && <span className="ml-1 text-[9px] uppercase tracking-widest bg-red-100 px-1 py-0.5">past due</span>}</td>
                    <td className={`py-1.5 ${u.pay_date < today ? "text-amber-700" : ""}`}>{fmt(u.pay_date)}{u.n === 1 && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-emerald-700">next</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {s.skipped.length > 0 && (
              <div className="mt-3 text-xs">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Skipped (no payroll)</div>
                {s.skipped.map((sk, i) => (
                  <div key={sk.skip_id} data-testid={`pay-calendar-skipped-${sk.skip_id}`} className="flex items-center gap-2 py-1 text-muted-foreground line-through decoration-muted-foreground/60">
                    <span className="font-mono">{sk.pay_date}</span><span className="no-underline">— {sk.reason}</span>
                    {!isClient && canSkip && i === 0 && <button data-testid={`pay-calendar-unskip-${sk.skip_id}`} onClick={() => unskip(s, sk)} className="ml-auto inline-flex items-center gap-1 no-underline border border-border px-1.5 py-0.5 hover:border-black" style={{ textDecoration: "none" }}><Undo2 className="h-3 w-3" /> Undo</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
